// SchemePay Reconciliation Dashboard - Multi-Format Export Engine
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ReconciledItem, ReconciliationSummary, ReportType, ExportFormat } from '../types/reconciliation';

export function filterItemsForReport(items: ReconciledItem[], reportType: ReportType): ReconciledItem[] {
  switch (reportType) {
    case 'exact_match':
      return items.filter((i) => i.status === 'Exact Match');
    case 'partial_match':
      return items.filter((i) => i.status === 'Partial Match');
    case 'over_billed':
      return items.filter((i) => i.status === 'Over Billed');
    case 'no_bill_reference':
      return items.filter((i) => i.status === 'No Bill Reference');
    case 'erp_only':
      return items.filter((i) => i.status === 'ERP Only');
    case 'refund':
      return items.filter((i) => i.status === 'Refunded');
    case 'combined_master':
    case 'daily_summary':
    default:
      return items;
  }
}

export function getReportTitle(reportType: ReportType): string {
  switch (reportType) {
    case 'exact_match':
      return 'Exact Match Reconciliation Report';
    case 'partial_match':
      return 'Partial Match (Under-Billed) Exception Report';
    case 'over_billed':
      return 'Over-Billed Variance Exception Report';
    case 'no_bill_reference':
      return 'Missing ERP Bill Reference Report';
    case 'erp_only':
      return 'ERP Only (Uncollected Bills) Report';
    case 'refund':
      return 'Refunded Transactions Audit Report';
    case 'daily_summary':
      return 'Daily Reconciliation Summary & KPIs';
    case 'combined_master':
    default:
      return 'SchemePay Combined Master Reconciliation Report';
  }
}

/**
 * Format items for export tabular data
 */
export function formatItemsForExport(items: ReconciledItem[]): Record<string, any>[] {
  return items.map((item, idx) => ({
    'S.No': idx + 1,
    'Payment ID': item.paymentId,
    'Customer Name': item.customerName,
    'Payment Date': item.paymentDate,
    'Razorpay Amount (INR)': item.razorpayAmount,
    'ERP Total Amount (INR)': item.erpTotalAmount,
    'Difference (INR)': item.differenceAmount,
    'ERP Bill Count': item.billCount,
    'ERP Bill Numbers': item.billNumbers.join('; '),
    'Reconciliation Status': item.status,
    'Refund Status': item.refundStatus,
    'Refund Amount (INR)': item.refundAmount,
    'Refund Date': item.refundDate,
    'Notes': item.aiNotes || '',
  }));
}

/**
 * Exports report in XLSX format
 */
export function exportToXLSX(
  items: ReconciledItem[],
  summary: ReconciliationSummary,
  reportType: ReportType
): void {
  const filtered = filterItemsForReport(items, reportType);
  const rows = formatItemsForExport(filtered);
  const title = getReportTitle(reportType);

  const wb = XLSX.utils.book_new();

  // If daily summary, add summary KPI sheet first
  if (reportType === 'daily_summary') {
    const summarySheetData = [
      ['Metric', 'Value'],
      ['Report Generated On', new Date().toLocaleString()],
      ['Total Razorpay Transactions', summary.totalRazorpayRecords],
      ['Total ERP Bills', summary.totalErpRecords],
      ['Total Reconciled Payment Groups', summary.totalGroups],
      ['Exact Matches', summary.exactMatches],
      ['Partial Matches', summary.partialMatches],
      ['Over Billed Cases', summary.overBilled],
      ['Missing ERP Bills (No Reference)', summary.noBillReference],
      ['ERP Only Records', summary.erpOnly],
      ['Refund Transactions', summary.refundedCount],
      ['Total Collection Amount (INR)', summary.totalCollectionAmount],
      ['Total Reconciled Amount (INR)', summary.totalReconciledAmount],
      ['Total Exception Amount (INR)', summary.totalExceptionAmount],
      ['Match Percentage', `${summary.matchPercentage}%`],
      ['Processing Time (ms)', summary.processingTimeMs],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summarySheetData);
    XLSX.utils.book_append_sheet(wb, wsSummary, 'KPI Summary');
  }

  const ws = XLSX.utils.json_to_sheet(rows);

  // Set column widths for clean readability
  ws['!cols'] = [
    { wch: 6 },  // S.No
    { wch: 16 }, // Payment ID
    { wch: 32 }, // Customer Name
    { wch: 20 }, // Payment Date
    { wch: 22 }, // Razorpay Amount
    { wch: 22 }, // ERP Total Amount
    { wch: 18 }, // Difference
    { wch: 14 }, // Bill Count
    { wch: 35 }, // Bill Numbers
    { wch: 20 }, // Status
    { wch: 14 }, // Refund Status
    { wch: 18 }, // Refund Amount
    { wch: 18 }, // Refund Date
    { wch: 45 }, // Notes
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Reconciliation Data');

  const fileName = `${title.replace(/[\s&()]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

/**
 * Exports report in CSV format
 */
export function exportToCSV(
  items: ReconciledItem[],
  reportType: ReportType
): void {
  const filtered = filterItemsForReport(items, reportType);
  const rows = formatItemsForExport(filtered);
  const title = getReportTitle(reportType);

  const ws = XLSX.utils.json_to_sheet(rows);
  const csvContent = XLSX.utils.sheet_to_csv(ws);

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${title.replace(/[\s&()]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Exports report in formatted PDF format
 */
export function exportToPDF(
  items: ReconciledItem[],
  summary: ReconciliationSummary,
  reportType: ReportType
): void {
  const filtered = filterItemsForReport(items, reportType);
  const title = getReportTitle(reportType);

  // Use landscape for enterprise data tables
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

  // Header banner
  doc.setFillColor(11, 27, 50); // Deep corporate navy (#0b1b32)
  doc.rect(0, 0, 842, 60, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('SCHEMEPAY RECONCILIATION DASHBOARD', 30, 32);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleString('en-IN')} | Department: Corporate Accounts`, 30, 48);

  // Subtitle
  doc.setTextColor(30, 41, 59);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(title, 30, 85);

  // Summary row
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(
    `Total Records: ${filtered.length} | Match Rate: ${summary.matchPercentage}% | Total Collections: INR ${summary.totalCollectionAmount.toLocaleString(
      'en-IN'
    )} | Exceptions: INR ${summary.totalExceptionAmount.toLocaleString('en-IN')}`,
    30,
    100
  );

  // Table Columns
  const tableHeaders = [
    'S.No',
    'Payment ID',
    'Customer Name',
    'Payment Date',
    'Razorpay Amt',
    'ERP Total Amt',
    'Diff',
    'Bills',
    'Bill Numbers',
    'Status',
  ];

  const tableData = filtered.map((item, idx) => [
    idx + 1,
    item.paymentId,
    item.customerName.length > 25 ? item.customerName.substring(0, 22) + '...' : item.customerName,
    item.paymentDate.split(' ')[0],
    `INR ${item.razorpayAmount.toLocaleString('en-IN')}`,
    `INR ${item.erpTotalAmount.toLocaleString('en-IN')}`,
    `INR ${item.differenceAmount.toLocaleString('en-IN')}`,
    item.billCount,
    item.billNumbers.join(', '),
    item.status,
  ]);

  autoTable(doc, {
    startY: 112,
    head: [tableHeaders],
    body: tableData,
    theme: 'grid',
    styles: {
      fontSize: 8,
      cellPadding: 4,
      font: 'helvetica',
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: [15, 76, 129], // Classic corporate blue
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'left',
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    didParseCell: (data) => {
      // Color status text in PDF
      if (data.column.index === 9 && data.section === 'body') {
        const val = String(data.cell.raw);
        if (val === 'Exact Match') {
          data.cell.styles.textColor = [16, 185, 129]; // Green
          data.cell.styles.fontStyle = 'bold';
        } else if (val === 'Partial Match') {
          data.cell.styles.textColor = [217, 119, 6]; // Amber
          data.cell.styles.fontStyle = 'bold';
        } else if (val === 'Over Billed') {
          data.cell.styles.textColor = [139, 92, 246]; // Purple
          data.cell.styles.fontStyle = 'bold';
        } else if (val === 'No Bill Reference') {
          data.cell.styles.textColor = [239, 68, 68]; // Red
          data.cell.styles.fontStyle = 'bold';
        } else if (val === 'ERP Only') {
          data.cell.styles.textColor = [99, 102, 241]; // Indigo
          data.cell.styles.fontStyle = 'bold';
        } else if (val === 'Refunded') {
          data.cell.styles.textColor = [14, 165, 233]; // Blue
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },
    margin: { left: 30, right: 30 },
  });

  const fileName = `${title.replace(/[\s&()]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
}

/**
 * Master dispatcher for report downloads
 */
export function triggerExport(
  items: ReconciledItem[],
  summary: ReconciliationSummary,
  reportType: ReportType,
  format: ExportFormat
): void {
  if (format === 'xlsx') {
    exportToXLSX(items, summary, reportType);
  } else if (format === 'csv') {
    exportToCSV(items, reportType);
  } else if (format === 'pdf') {
    exportToPDF(items, summary, reportType);
  }
}
